import { type User } from "@microsoft/microsoft-graph-types";
import {
  type Appointment,
  type CreateOpportunityPayload,
  type Customer,
  type CustomerConsent,
  type PreferredContactMethod,
  type Task,
  type UserInfo,
  type hfb,
} from "@repo/api";
import { type Values, assertUnreachable } from "@repo/utils";
import { type ActionType, createAction, getType } from "typesafe-actions";

type Step = "SELECT_PROJECT" | "NEW_PROJECT" | "TITLE" | "CONTACT_METHOD";

export interface NewProjectState {
  currentStep: Step;
  customerInfo?: Customer;
  associatedTask?: Task;
  associatedDropInAppointment?: Appointment;
  opportunityInfo: CreateOpportunityPayload["opportunityInfo"];
  assignedCoworker: UserInfo | undefined;
  assignedTeam: number | undefined;
  profile?: User;
  contactMethod?: Values<typeof PreferredContactMethod>;
  customerConsents?: CustomerConsent[];
  manualConsents?: CustomerConsent[];
  sendConfirmation?: boolean;
  customerSearchValue: string;
}

export const NewProjectStateDefault: NewProjectState = {
  currentStep: "SELECT_PROJECT",
  opportunityInfo: {
    title: "",
    source: "SLA",
    businessUnit: {
      buType: "STO",
    },
    customerLocale: "SE",
    CustomerInterestIndex: "Medium",
    CustomerReadiness: "Exploring",
    hfb: [],
  },
  customerInfo: undefined,
  associatedTask: undefined,
  assignedCoworker: undefined,
  assignedTeam: undefined,
  profile: undefined,
  contactMethod: undefined,
  customerConsents: undefined,
  manualConsents: undefined,
  sendConfirmation: false,
  customerSearchValue: "",
  associatedDropInAppointment: undefined,
};

export type NewProjectStateActions = typeof newProjectStateActions;
export const newProjectStateActions = {
  setStep: createAction("SET_STEP")<Step>(),
  setTitle: createAction("SET_TITLE")<string>(),
  setDepartments: createAction("SET_DEPARTMENTS")<hfb[]>(),
  setAssignedCoworker: createAction("SET_ASSIGNED_COWORKER")<
    UserInfo | undefined
  >(),
  setAssignedTeam: createAction("SET_ASSIGNED_TEAM")<number | undefined>(),
  setProfile: createAction("SET_PROFILE")<User>(),
  setContactMethod:
    createAction("SET_CONTACT_METHOD")<Values<typeof PreferredContactMethod>>(),
  setCustomerConsents: createAction("SET_CUSTOMER_CONSENTS")<
    CustomerConsent[] | undefined
  >(),

  setManualConsents: createAction("SET_MANUAL_CONSENTs")<
    CustomerConsent[] | undefined
  >(),

  setConfirmation: createAction("SET_CONFIRMATION")<boolean>(),
  setCustomerSearchValue: createAction("SET_CUSTOMER_SEARCH_VALUE")<string>(),
  resetNewProject: createAction("RESET_NEW_PROJECT")(),
};

export function newProjectStateReducer(
  state: NewProjectState,
  action: ActionType<NewProjectStateActions>
): NewProjectState {
  switch (action.type) {
    case getType(newProjectStateActions.setStep):
      return {
        ...state,
        currentStep: action.payload,
      };
    case getType(newProjectStateActions.setTitle):
      return {
        ...state,
        opportunityInfo: { ...state.opportunityInfo, title: action.payload },
      };

    case getType(newProjectStateActions.setDepartments):
      return {
        ...state,
        opportunityInfo: { ...state.opportunityInfo, hfb: action.payload },
      };

    case getType(newProjectStateActions.setAssignedCoworker):
      return {
        ...state,
        assignedCoworker: action.payload,
        assignedTeam: undefined,
      };
    case getType(newProjectStateActions.setAssignedTeam):
      return {
        ...state,
        assignedTeam: action.payload,
        assignedCoworker: undefined,
      };
    case getType(newProjectStateActions.setProfile):
      return {
        ...state,
        profile: action.payload,
      };

    case getType(newProjectStateActions.setContactMethod):
      return {
        ...state,
        contactMethod: action.payload,
      };
    case getType(newProjectStateActions.setCustomerConsents):
      return {
        ...state,
        customerConsents: action.payload,
      };

    case getType(newProjectStateActions.setManualConsents):
      return {
        ...state,
        manualConsents: action.payload,
      };

    case getType(newProjectStateActions.setConfirmation):
      return {
        ...state,
        sendConfirmation: action.payload,
      };

    case getType(newProjectStateActions.setCustomerSearchValue):
      return {
        ...state,
        customerSearchValue: action.payload,
      };
    case getType(newProjectStateActions.resetNewProject):
      return {
        ...state,
        contactMethod: undefined,
        customerConsents: undefined,
        manualConsents: undefined,
      };
  }

  assertUnreachable(action);
}
